class ProductGrid extends HTMLElement {
  constructor () {
    super()

    this.state = {
      root: window.theme.tagalys.results.stateLayer.state,
    }

    // Store bound handlers for cleanup
    this._boundHandlers = {
      handleProductsUpdated: (products) => this.render(products),
      handleShopifyProductsFetched: () => this.fallbackRender(),
    }
  }

  connectedCallback () {
    this.subscribeToEvents()
  }

  disconnectedCallback () {
    this.unsubscribeFromEvents()
  }

  subscribeToEvents () {
    window.theme.tagalys.results.eventBus.subscribe('results:state:products:updated', this._boundHandlers.handleProductsUpdated)
    window.theme.tagalys.results.eventBus.subscribe('results:shopify:products:fetched', this._boundHandlers.handleShopifyProductsFetched)
  }

  unsubscribeFromEvents () {
    window.theme.tagalys.results.eventBus.unsubscribe('results:state:products:updated', this._boundHandlers.handleProductsUpdated)
    window.theme.tagalys.results.eventBus.unsubscribe('results:shopify:products:fetched', this._boundHandlers.handleShopifyProductsFetched)
  }

  _returnPromosToHiddenContainer () {
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById('pre-rendered-promos')
    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }
    const insertedPromoElements = gridContainer.querySelectorAll('[data-inserted-promo="true"]')
    insertedPromoElements.forEach(promoElement => {
      const originalPosition = promoElement.dataset.originalPosition
      if (!originalPosition) {
        return
      }
      const originalWrapper = preRenderedPromosContainer.querySelector(`[data-promo-position="${originalPosition}"]`)
      if (originalWrapper) {
        delete promoElement.dataset.insertedPromo
        delete promoElement.dataset.originalPosition
        originalWrapper.appendChild(promoElement)
      }
    })
  }

  render (products) {
    const count = products.length
    const gridContainer = this.productGrid
    let renderPromos = true

    const requestType = window.theme.tagalys.results.SDK.helpers.getRequestParams()?.action

    if (requestType === "APPLY_FILTER" || window.theme.tagalys.results.SDK.helpers.hasAnyFiltersApplied()) {
      renderPromos = false
    }


    if (!gridContainer) {
      return
    }

    this._returnPromosToHiddenContainer()

    gridContainer.innerHTML = ''

    const potentialPromos = this._getPotentialPromosForCurrentPage()
    if (count === 0 && potentialPromos.length === 0) {
      gridContainer.insertAdjacentHTML('beforeend', `
        <div class="no-results">
          <p>No results found</p>
        </div>
      `)
      return
    }


    const pickupScript = document.getElementById('pickup-only-products');
    let pickupOnlyIds = [];

    if (pickupScript) {
      try {
        // Use textContent to avoid parsing any markup
        pickupOnlyIds = JSON.parse(pickupScript.textContent);
      } catch (error) {
        console.error('Failed to parse pickup-only products JSON', error);
      }
    }

    const pickupOnlyIdSet = new Set(
      pickupOnlyIds.map((id) => String(id))
    )

    products.forEach((product) => {
      const productTemplate = this.templateProductCard
      if (!productTemplate) {
        return
      }
      const clone = document.importNode(productTemplate.content, true)
      const productCard = clone.querySelector('product-card')
      if (!productCard) {
        return
      }

      // Normalize the product.id to string to match the Set content
      const is_pickup_only = pickupOnlyIdSet.has(String(product.id));

      // Add a flag into the serialized product data
      const productWithFlags = {
        ...product,
        is_pickup_only,
      };

      productCard.setAttribute('data-prop-data', JSON.stringify(productWithFlags))
      gridContainer.appendChild(clone)
    })

    if (renderPromos) {
      this._insertPromoCards()
    }
  }

  async fallbackRender () {
    this.productGrid.innerHTML = ''
    try {
      const url = `/collections/${this.collectionHandle}?section_id=${this.fallbackSectionId}`
      const fullUrl = this.pageParam ? `${url}&page=${this.pageParam}` : url
      const response = await fetch(fullUrl)

      if (!response.ok) throw new Error('Failed to fetch fallback product grid section')
      const text = await response.text()
      const html = new DOMParser().parseFromString(text, 'text/html')
      const gridHTML = html.querySelector(`[data-product-grid-external-el="fallbackProductGrid"]`).innerHTML

      this.innerHTML = gridHTML
    } catch (error) {
      console.error('Failed to fetch swatches section:', error)
    }
  }

  get productGrid () {
    return this.querySelector('.vbf-product-grid')
  }

  get templateProductCard () {
    return document.getElementById('templateProductCard')
  }

  get templateHeroCard () {
    return document.getElementById('templateHeroCard')
  }

  get fallbackSectionId () {
    return 'fallback-product-grid'
  }

  get collectionHandle () {
    return this.getAttribute('data-collection-handle')
  }

  get pageParam () {
    return new URLSearchParams(window.location.search).get('page')
  }

  get allPromosData () {
    if (!this._allPromosData) {
      const promoDataEl = document.querySelector('[data-promos]')
      try {
        this._allPromosData = promoDataEl ? JSON.parse(promoDataEl.textContent) : []
      } catch (e) {
        console.error('Failed to parse promo data', e)
        this._allPromosData = []
      }
    }
    return this._allPromosData
  }

  _getPotentialPromosForCurrentPage () {
    const currentPage = this.state.root.pagination.page || 1
    const itemsPerPage = window.theme.resultsConfig.productsPerPage || 40
    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage
    const allData = this.allPromosData || []
    return allData.filter(p => p.position >= firstItemIndexOnPage && p.position <= lastItemIndexOnPage)
  }

  _insertPromoCards () {
    const activeFacets = this.state.root.activeFacets || []
    if (activeFacets.length > 0) {
      return
    }

    const currentPage = this.state.root.pagination.page || 1
    const itemsPerPage = window.theme.resultsConfig.productsPerPage || 40
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById('pre-rendered-promos')

    if (!gridContainer || !preRenderedPromosContainer) {
      return
    }

    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage

    const promoWrappersToShow = Array.from(preRenderedPromosContainer.children)
      .filter(promoWrapper => {
        if (!(promoWrapper instanceof HTMLElement) || !promoWrapper.dataset || !promoWrapper.dataset.promoPosition) return false
        const position = parseInt(promoWrapper.dataset.promoPosition, 10)
        return !isNaN(position) && position >= firstItemIndexOnPage && position <= lastItemIndexOnPage
      })
      .sort((a, b) => parseInt(a.dataset.promoPosition, 10) - parseInt(b.dataset.promoPosition, 10))

    promoWrappersToShow.forEach(promoWrapper => {
      const position = parseInt(promoWrapper.dataset.promoPosition, 10)
      const promoElement = promoWrapper.firstElementChild

      if (!promoElement) {
        return
      }

      promoElement.dataset.insertedPromo = 'true'
      promoElement.dataset.originalPosition = position

      const targetPageIndex = position - firstItemIndexOnPage
      const elementAtIndex = gridContainer.children[targetPageIndex]

      if (elementAtIndex) {
        gridContainer.insertBefore(promoElement, elementAtIndex)
      } else {
        gridContainer.appendChild(promoElement)
      }

      const playlist = document.querySelector('bam-playlist')

      if (playlist && playlist.hasAttribute('playlist-id')) {
        const currentId = playlist.getAttribute('playlist-id')

        // Briefly remove and re-add the ID
        playlist.removeAttribute('playlist-id')
        setTimeout(() => {
          playlist.setAttribute('playlist-id', currentId)
        }, 500)
      }
    })
  }
}

customElements.define('product-grid', ProductGrid)
